/* Iter 103.0 Phase 2 · Curated Keyword Synonym Map
   ────────────────────────────────────────────────
   Maps route paths → array of alternative search terms ("keywords" /
   "synonyms"). Boosts search recall for common user vocabulary that
   doesn't literally appear in a nav item's display label.

   Storage decision (REEDOS approval 2026-02-11, decision #6):
   Stored SEPARATELY from `navRegistry.js` so the two can evolve
   independently. Nav labels are locked by IA governance (menu IA
   v3 guard); keywords are living UX data refined based on how
   users actually search.

   Design principles:
     • Recall > precision. False-positive risk is bounded by the
       8-hit cap in `menuSearch.filterMenuHits()`.
     • Include Indian-English variants ("regularisation" vs
       "regularization").
     • Include common abbreviations power users type ("fnf",
       "ctc", "cf", "mar").
     • Include vocabulary from adjacent domains ("payslip" for
       "salary slip", "hire" for "onboard").
     • DO NOT re-list the display label itself — the label is
       already matched by `filterMenuHits()` in higher-priority
       scoring bands. Adding it here is noise.

   Governance:
     • Every KEY in this map MUST correspond to a route in
       `navRegistry.NAV_GROUPS`, `navRegistry.HIDDEN_ROUTES`, or the
       Dashboard entry. Drift detection via `menuSearch.js` unit
       tests (Iter 103.0 Phase 2).

   Iter 103.0 v1: 46 mappings, curated by E1 based on the
   navigation inventory and inferred user vocabulary.
*/

const SEARCH_KEYWORDS = {
    // ── ESS / Self-service ──
    "/attendance/me":                   ["attendance", "check in", "check out", "punch in", "punch out", "my hours"],
    "/leave/portal":                    ["leave", "apply leave", "my leave", "leave balance", "leave request", "leave application"],
    // Iter 110 · P1.D — Comp-Off ESS discoverability via global search.
    "/leave/comp-off":                   ["comp off", "comp-off", "compensatory off", "comp off request", "credit", "weekly off work", "holiday work"],
    "/regularization/my":               ["my regularization", "my regularisation", "correction request", "missed punch"],
    "/attendance/my-month":             ["my month", "monthly attendance", "regularise", "pending dates", "finalised"],
    "/admin/attendance/month-close":    ["month close", "finalise attendance", "finalize month", "attendance lock"],
    "/admin/attendance/payroll-adjustments": ["arrears", "recovery", "attendance adjustment", "post payroll"],
    "/ess/me/payslips":                 ["payslip", "salary slip", "pay slip", "my salary"],
    "/ess/me":                          ["my profile", "personal details", "contact", "address"],
    "/ess/my-activity":                 ["activity", "my activity"],
    "/ess/my-desktop-activity":         ["presence", "my presence", "agent activity"],

    // ── Employees ──
    "/employees":                       ["employee", "employees", "people", "staff", "directory", "workforce list"],
    "/admin/onboard":                   ["onboard", "onboarding", "add employee", "hire", "new employee", "add user", "create user", "create employee"],

    // ── Workforce · Attendance ──
    "/admin/attendance/shifts":         ["shift", "shifts", "timings", "working hours", "schedule"],
    "/admin/attendance/policies":       ["policy", "policies", "attendance policy", "rules"],
    "/admin/attendance/assignments":    ["assignment", "assign shift", "assign policy", "policy assignment"],
    "/attendance/anomalies":            ["anomaly", "anomalies", "exception", "exceptions", "issue", "mismatch"],
    "/regularization/requests":         ["regularization", "regularisation", "correction", "missed punch"],
    "/attendance/workspace":            ["attendance workspace", "team attendance", "manager attendance"],
    "/admin/attendance/hr-dashboard":   ["hr attendance", "hr dashboard", "attendance dashboard"],

    // ── Workforce · Attendance Reports ──
    "/admin/attendance/reports/monthly-register":  ["monthly register", "attendance register", "mar"],
    "/admin/attendance/reports/daily-summary":     ["daily summary", "daily attendance"],
    "/admin/attendance/reports/monthly-summary":   ["monthly summary", "month attendance"],
    "/admin/attendance/reports/anomalies":         ["exception report", "anomaly report"],
    "/admin/attendance/reports/regularization":    ["regularization report", "correction report"],
    "/admin/attendance/reports/trends":            ["attendance trends", "trends"],

    // ── Workforce · Leave Administration (incl. hidden orphans) ──
    "/admin/leave/holiday-calendars":   ["holiday", "holiday list", "holidays", "festival", "public holiday", "calendar"],
    "/admin/leave/balances":            ["leave balance", "balance"],
    "/admin/leave/ledger":              ["leave ledger", "ledger explorer"],
    "/admin/leave/accrual":             ["accrual", "leave accrual"],
    "/admin/leave/carry-forward":       ["carry forward", "cf", "leave carry forward"],
    "/admin/leave/configuration":       ["leave configuration", "leave types", "workflow", "leave setup", "adjustment reason", "reason master", "fiscal year"],

    // ── Workforce · Leave Operations ──
    "/leave/approval-dashboard":        ["approval", "approvals", "approval dashboard", "leave dashboard", "pending approvals"],
    "/leave/calendar":                  ["leave calendar", "team leaves"],
    "/leave/reports/balance":           ["leave balance report"],
    "/leave/reports/utilization":       ["leave utilization"],
    "/leave/reports/pending-approval":  ["pending approval", "pending leaves"],

    // ── Payroll ──
    "/admin/payroll/runs":              ["payroll", "run payroll", "payroll run"],
    "/admin/payroll/period-close":      ["period close", "close period", "payroll close"],
    "/admin/payroll/assignments":       ["payroll", "salary setup", "ctc", "ctc setup", "salary structure", "compensation"],
    "/admin/payroll/components":        ["payroll", "salary components", "earnings", "deductions", "allowances"],
    "/admin/payroll/structures":        ["payroll structure", "salary structure"],
    "/admin/payroll/webhooks":          ["payroll events", "webhook"],
    // Iter 111.1 UAT discoverability fix (2026-02-12): business users searched
    // for "Professional Tax" / "PT" / "PT Master" and couldn't find the screen.
    // Keywords chosen for recall — abbreviation, expansion, and combinations.
    "/admin/payroll/compliance/pt-master": ["professional tax", "pt master", "pt", "professional tax master", "pt slab", "pt slabs", "state professional tax"],
    "/admin/loans":                     ["loan", "loans", "advance", "advances", "salary advance"],
    "/admin/assets":                    ["asset", "assets", "allocation", "asset assignment"],
    "/admin/exit":                      ["exit", "resignation", "fnf", "full and final", "separation", "offboarding"],
    "/admin/compliance/health":         ["compliance dashboard", "compliance health"],
    "/admin/compliance/tds-challans":   ["tds challan", "challan"],
    "/admin/compliance/tds-returns":    ["tds return", "return"],
    "/admin/compliance/tds-reconciliation": ["tds reconciliation", "reconciliation"],

    // ── Workforce Insights ──
    "/workforce/desktop-activity":      ["desktop presence", "agent", "agent activity", "presence"],
    "/manager/health":                  ["team health", "manager dashboard", "team status"],
    "/manager/team-activity":           ["team activity"],
    "/admin/workforce/analytics":       ["workforce analytics", "insights", "analytics"],
    "/admin/desktop-agent/registrations": ["desktop agent", "agent enrolment", "agent registration"],
};

export default SEARCH_KEYWORDS;
